import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon, FacebookIcon, TelegramIcon } from '@/components/icons';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityName: string;
  /** Optional custom share URL. Defaults to current page URL if not provided */
  shareUrl?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  entityName,
  shareUrl: customShareUrl,
}) => {
  const [copied, setCopied] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Use custom share URL or default to current page URL
  const shareUrl = customShareUrl || (typeof window !== 'undefined' ? window.location.href : '');


  // Check scroll state
  const checkScrollState = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the container is rendered
      setTimeout(checkScrollState, 100);
    }
  }, [isOpen]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      color: '#25D366',
      icon: (
        <svg className="w-7 h-7" fill="#25D366" viewBox="0 0 640 640">
          <path d="M476.9 161.1C435 119.1 379.2 96 319.9 96C197.5 96 97.9 195.6 97.9 318C97.9 357.1 108.1 395.3 127.5 429L96 544L213.7 513.1C246.1 530.8 282.6 540.1 319.8 540.1L319.9 540.1C442.2 540.1 544 440.5 544 318.1C544 258.8 518.8 203.1 476.9 161.1zM319.9 502.7C286.7 502.7 254.2 493.8 225.9 477L219.2 473L149.4 491.3L168 423.2L163.6 416.2C145.1 386.8 135.4 352.9 135.4 318C135.4 216.3 218.2 133.5 320 133.5C369.3 133.5 415.6 152.7 450.4 187.6C485.2 222.5 506.6 268.8 506.5 318.1C506.5 419.9 421.6 502.7 319.9 502.7zM421.1 364.5C415.6 361.7 388.3 348.3 383.2 346.5C378.1 344.6 374.4 343.7 370.7 349.3C367 354.9 356.4 367.3 353.1 371.1C349.9 374.8 346.6 375.3 341.1 372.5C308.5 356.2 287.1 343.4 265.6 306.5C259.9 296.7 271.3 297.4 281.9 276.2C283.7 272.5 282.8 269.3 281.4 266.5C280 263.7 268.9 236.4 264.3 225.3C259.8 214.5 255.2 216 251.8 215.8C248.6 215.6 244.9 215.6 241.2 215.6C237.5 215.6 231.5 217 226.4 222.5C221.3 228.1 207 241.5 207 268.8C207 296.1 226.9 322.5 229.6 326.2C232.4 329.9 268.7 385.9 324.4 410C359.6 425.2 373.4 426.5 391 423.9C401.7 422.3 423.8 410.5 428.4 397.5C433 384.5 433 373.4 431.6 371.1C430.3 368.6 426.6 367.2 421.1 364.5z"/>
        </svg>
      ),
      url: `https://wa.me/?text=${encodeURIComponent(`Check out ${entityName}! ${shareUrl}`)}`,
    },
    {
      name: 'X',
      color: '#ffffff',
      icon: (
        <svg className="w-7 h-7" fill="#ffffff" viewBox="0 0 640 640">
          <path d="M453.2 112L523.8 112L369.6 288.2L551 528L409 528L297.7 382.6L170.5 528L99.8 528L264.7 339.5L90.8 112L236.4 112L336.9 244.9L453.2 112zM428.4 485.8L467.5 485.8L215.1 152L173.1 152L428.4 485.8z"/>
        </svg>
      ),
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${entityName}!`)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Facebook',
      color: '#1877F2',
      icon: <FacebookIcon className="w-7 h-7 text-[#1877F2]" />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Telegram',
      color: '#229ED9',
      icon: <TelegramIcon className="w-7 h-7 text-[#229ED9]" />,
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out ${entityName}!`)}`,
    },
    {
      name: 'Email',
      color: '#EA4335',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="#EA4335" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
        </svg>
      ),
      url: `mailto:?subject=${encodeURIComponent(`Check out ${entityName}!`)}&body=${encodeURIComponent(`I thought you'd love this: ${shareUrl}`)}`,
    },
  ];

  const handleShare = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop with blur animation */}
        <motion.div
          initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
          animate={{ opacity: 1, backdropFilter: 'blur(12px)' }}
          exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-black/80"
          onClick={onClose}
        />

        {/* Modal with spring animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 40 }}
          transition={{ 
            type: "spring",
            damping: 25,
            stiffness: 300,
            duration: 0.4
          }}
          className="relative bg-black border border-gray-800/60 rounded-3xl p-8 max-w-sm w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <motion.button
            onClick={onClose}
            initial={{ opacity: 0, rotate: -90 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            className="absolute top-5 right-5 z-10 p-2 bg-gray-900/80 hover:bg-gray-800/80 rounded-full text-gray-400 hover:text-white transition-colors duration-200"
          >
            <XIcon className="w-4 h-4" />
          </motion.button>

          {/* Header */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <motion.h2
              className="text-2xl font-serif font-medium text-white mb-2 tracking-tight"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
            >
              Spread the Word
            </motion.h2>
            <motion.p
              className="text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              Share <span className="text-white font-medium">{entityName}</span> with your friends
            </motion.p>
          </motion.div>

          {/* Copy Link Section */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 bg-gray-900/60 border border-gray-800/50 rounded-2xl p-3">
              <div className="flex-1 truncate text-sm text-gray-400 pl-2">
                {shareUrl}
              </div>
              <motion.button
                onClick={handleCopyLink}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  copied
                    ? 'bg-white text-black'
                    : 'bg-gray-800 hover:bg-gray-700 text-white'
                }`}
              >
                <motion.span
                  key={copied ? 'copied' : 'copy'}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </motion.span>
              </motion.button>
            </div>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">or share via</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" />
          </motion.div>

          {/* Social Share Options - Horizontal Scroll */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
            className="relative"
          >
            {/* Left Scroll Indicator */}
            <AnimatePresence>
              {canScrollLeft && (
                <motion.button
                  onClick={() => scroll('left')}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/80 hover:bg-gray-800/90 border border-gray-700/50 transition-all duration-200 shadow-lg"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
            
            {/* Right Scroll Indicator */}
            <AnimatePresence>
              {canScrollRight && (
                <motion.button
                  onClick={() => scroll('right')}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/80 hover:bg-gray-800/90 border border-gray-700/50 transition-all duration-200 shadow-lg"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
            
            {/* Scrollable Icons Container */}
            <div 
              ref={scrollContainerRef}
              onScroll={checkScrollState}
              className="flex gap-3 py-2 overflow-x-auto scrollbar-hide px-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {shareOptions.map((option, index) => (
                <motion.button
                  key={option.name}
                  onClick={() => handleShare(option.url)}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    delay: 0.4 + index * 0.05,
                    type: "spring",
                    damping: 15,
                    stiffness: 300
                  }}
                  whileHover={{ 
                    scale: 1.1,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.92 }}
                  className="flex items-center justify-center p-3.5 rounded-2xl bg-gray-900/50 hover:bg-gray-800/60 border border-gray-800/30 hover:border-gray-700/50 text-gray-400 hover:text-white transition-all duration-300 flex-shrink-0"
                >
                  {option.icon}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ShareModal;
